import { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Button, List, ListItemButton,
  ListItemIcon, ListItemText, Divider, Badge, Collapse,
  CircularProgress, alpha,
} from '@mui/material';
import {
  Create, Inbox as InboxIcon, Send as SentIcon, Drafts as DraftsIcon,
  Schedule as ScheduledIcon, Celebration, BarChart,
  Settings as SettingsIcon, ExpandLess, ExpandMore,
  Tune, Description, SmartToy, Email as EmailIcon, WhatsApp,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { TYPE } from '@/theme/typography';
import { messagingApi } from '@/api/endpoints/messaging';
import PageHelpIcon from '@/components/PageHelpIcon';

// Lazy-load pages to keep bundle small
const ComposePage = lazy(() => import('./pages/ComposePage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const SentPage = lazy(() => import('./pages/SentPage'));
const DraftsPage = lazy(() => import('./pages/DraftsPage'));
const ScheduledPage = lazy(() => import('./pages/ScheduledPage'));
const GreetingsPage = lazy(() => import('./pages/GreetingsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const SettingsProvidersPage = lazy(() => import('./pages/settings/ProvidersPage'));
const SettingsTemplatesPage = lazy(() => import('./pages/settings/TemplatesPage'));
const SettingsAutomationPage = lazy(() => import('./pages/settings/AutomationPage'));
const SettingsWhatsAppTemplatesPage = lazy(() => import('./pages/settings/WhatsAppTemplatesPage'));

// ─── Sidebar Section Types ──────────────────────────────────

type NavItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
};

type NavSection = {
  key: string;
  label?: string;
  items: NavItem[];
};

const SIDEBAR_WIDTH = 220;

// ─── Page Loader ────────────────────────────────────────────

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────

// Map URL sub-path ↔ activePage key. Keeps the URL as the source of truth
// so browser back/forward works and links like /messaging/greetings are
// shareable. Anything unknown drops back to compose.
const URL_TO_KEY: Record<string, string> = {
  '': 'compose',
  'compose': 'compose',
  'inbox': 'inbox',
  'sent': 'sent',
  'drafts': 'drafts',
  'scheduled': 'scheduled',
  'greetings': 'greetings',
  'announcements': 'announcements',
  'analytics': 'analytics',
  'settings/providers': 'settings-providers',
  'settings/templates': 'settings-templates',
  'settings/automation': 'settings-automation',
  'settings/whatsapp-templates': 'settings-whatsapp-templates',
};
const KEY_TO_URL: Record<string, string> = Object.fromEntries(
  Object.entries(URL_TO_KEY).map(([url, key]) => [key, url]),
);

export default function MessagingPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive activePage from the URL (single source of truth).
  const subPath = location.pathname.replace(/^\/messaging\/?/, '').replace(/\/$/, '');
  const activePage = URL_TO_KEY[subPath] || 'compose';
  const [settingsOpen, setSettingsOpen] = useState(activePage.startsWith('settings-'));

  // Keep the settings accordion open whenever the URL lands on a settings tab.
  useEffect(() => {
    if (activePage.startsWith('settings-')) setSettingsOpen(true);
  }, [activePage]);

  const setActivePage = (key: string) => {
    const url = KEY_TO_URL[key] || '';
    navigate(url ? `/messaging/${url}` : '/messaging');
  };

  // Sidebar badge counts — one endpoint, one query, three numbers.
  const { data: counts } = useQuery({
    queryKey: ['messaging/counts'],
    queryFn: () => messagingApi.getCounts().then((r) => r.data),
    staleTime: 30_000,
  });
  const draftCount = counts?.drafts || 0;
  const scheduledCount = counts?.scheduled || 0;
  const unreadCount = counts?.unread || 0;

  // ── Navigation sections ──

  const sections: NavSection[] = [
    {
      key: 'mailbox',
      items: [
        { key: 'compose', label: 'Compose', icon: <Create sx={{ fontSize: 20 }} /> },
        { key: 'inbox', label: 'Inbox', icon: <InboxIcon sx={{ fontSize: 20 }} />, badge: unreadCount },
        { key: 'sent', label: 'Sent', icon: <SentIcon sx={{ fontSize: 20 }} /> },
        { key: 'drafts', label: 'Drafts', icon: <DraftsIcon sx={{ fontSize: 20 }} />, badge: draftCount },
        { key: 'scheduled', label: 'Scheduled', icon: <ScheduledIcon sx={{ fontSize: 20 }} />, badge: scheduledCount },
      ],
    },
    {
      key: 'tools',
      label: 'Tools',
      items: [
        { key: 'greetings', label: 'Greetings', icon: <Celebration sx={{ fontSize: 20 }} /> },
        { key: 'announcements', label: 'Announcements', icon: <CampaignIcon sx={{ fontSize: 20 }} /> },
        { key: 'analytics', label: 'Analytics', icon: <BarChart sx={{ fontSize: 20 }} /> },
      ],
    },
  ];

  const settingsItems: NavItem[] = [
    { key: 'settings-providers', label: 'Providers', icon: <Tune sx={{ fontSize: 18 }} /> },
    { key: 'settings-templates', label: 'Templates', icon: <Description sx={{ fontSize: 18 }} /> },
    { key: 'settings-whatsapp-templates', label: 'WhatsApp Templates', icon: <WhatsApp sx={{ fontSize: 18 }} /> },
    { key: 'settings-automation', label: 'Automation', icon: <SmartToy sx={{ fontSize: 18 }} /> },
  ];

  const isSettingsPage = activePage.startsWith('settings-');

  const handleNav = (key: string) => {
    setActivePage(key);
    if (!key.startsWith('settings-')) {
      setSettingsOpen(false);
    }
  };

  // ── Render content ──

  const renderContent = () => {
    switch (activePage) {
      case 'compose': return <ComposePage />;
      case 'inbox': return <InboxPage />;
      case 'sent': return <SentPage />;
      case 'drafts': return <DraftsPage />;
      case 'scheduled': return <ScheduledPage />;
      case 'greetings': return <GreetingsPage />;
      case 'announcements': return <AnnouncementsPage />;
      case 'analytics': return <AnalyticsPage />;
      case 'settings-providers': return <SettingsProvidersPage />;
      case 'settings-templates': return <SettingsTemplatesPage />;
      case 'settings-whatsapp-templates': return <SettingsWhatsAppTemplatesPage />;
      case 'settings-automation': return <SettingsAutomationPage />;
      default: return <ComposePage />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      {/* ── Page Header ─────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EmailIcon color="primary" sx={{ fontSize: 28 }} />
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h4" sx={{ fontWeight: TYPE.bold }}>Messaging</Typography>
            <PageHelpIcon pageKey="messaging" />
          </Stack>
        </Stack>
        {activePage !== 'compose' && (
          <Button
            variant="contained"
            size="small"
            startIcon={<Create />}
            onClick={() => setActivePage('compose')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Compose
          </Button>
        )}
      </Stack>

      {/* ── Body: Sidebar + Content ─────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 0 }}>
        {/* ── LEFT SIDEBAR ── */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            bgcolor: (t) => alpha(t.palette.background.default, 0.5),
            pr: 0.5,
          }}
        >
          {/* Compose button (prominent) */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<Create />}
            onClick={() => setActivePage('compose')}
            sx={{
              mb: 1, py: 1.2, borderRadius: 3,
              textTransform: 'none', fontWeight: TYPE.semibold,
              fontSize: TYPE.md,
              boxShadow: activePage === 'compose' ? 4 : 1,
            }}
          >
            Compose
          </Button>

          {/* Navigation sections */}
          {sections.map((section) => (
            <Box key={section.key}>
              {section.label && (
                <Typography
                  sx={{
                    px: 1.5, pt: 1.5, pb: 0.5,
                    fontSize: TYPE.xs, fontWeight: TYPE.semibold,
                    color: 'text.secondary', textTransform: 'uppercase',
                    letterSpacing: 0.8,
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <List dense disablePadding>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.key}
                    selected={activePage === item.key}
                    onClick={() => handleNav(item.key)}
                    sx={{
                      borderRadius: 2, mx: 0.5, mb: 0.25,
                      minHeight: 38,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: TYPE.sm, fontWeight: activePage === item.key ? TYPE.semibold : TYPE.medium }}
                    />
                    {item.badge ? (
                      <Badge
                        badgeContent={item.badge}
                        color="error"
                        sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                      />
                    ) : null}
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}

          <Divider sx={{ my: 1 }} />

          {/* Settings (collapsible) */}
          <ListItemButton
            onClick={() => setSettingsOpen(!settingsOpen)}
            sx={{ borderRadius: 2, mx: 0.5, minHeight: 38 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SettingsIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{ fontSize: TYPE.sm, fontWeight: isSettingsPage ? TYPE.semibold : TYPE.medium }}
            />
            {settingsOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          </ListItemButton>
          <Collapse in={settingsOpen} timeout="auto">
            <List dense disablePadding sx={{ pl: 1 }}>
              {settingsItems.map((item) => (
                <ListItemButton
                  key={item.key}
                  selected={activePage === item.key}
                  onClick={() => handleNav(item.key)}
                  sx={{
                    borderRadius: 2, mx: 0.5, mb: 0.25, minHeight: 34,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: TYPE.xs, fontWeight: activePage === item.key ? TYPE.semibold : TYPE.medium }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </Box>

        {/* ── MAIN CONTENT ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', pl: 3, pr: 1, pb: 2 }}>
          <Suspense fallback={<PageLoader />}>
            {renderContent()}
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
